<template>
  <div>
    <!-- modals -->
    <b-modal v-model="showImportModal" title="Import doses from a CSV file" @ok="importDoses">
      <b-form ref="form">
        <b-form-group>
          <input :key="importKey" type="file" accept=".csv" @change="handleFileUpload($event)" />
        </b-form-group>
        <hr />
        <b-form-group>
          <label class="form-label">Which source was the CSV file exported from?</label>
          <b-form-radio-group v-model="importSource">
            <b-form-radio v-for="(item, key) in sources" :key="key" name="importSource" :value="key"
              ><span style="margin-left: 10px">{{ item }}</span>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <br />
        <b-form-group>
          <label class="form-label">Which format is the date and time provided in?</label>
          <b-form-radio-group v-model="importFormat">
            <b-form-radio v-for="(item, key) in formats" :key="key" name="importFormat" :value="key"
              ><span style="margin-left: 10px">{{ item }}</span>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <template v-if="items.length > 0">
          <br />
          <b-form-group>
            <label class="form-label"
              >The table already has doses. What should the import do?</label
            >
            <b-form-radio-group v-model="importMode">
              <b-form-radio name="importMode" value="replace"
                ><span style="margin-left: 10px"
                  >Replace all table rows ({{ items.length }} existing)</span
                >
              </b-form-radio>
              <b-form-radio name="importMode" value="add"
                ><span style="margin-left: 10px">Add to existing doses</span>
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
      </b-form>
      <div v-if="parsed" class="overflow-auto">
        <h3>Preview</h3>
        <table style="width: 100%">
          <thead>
            <tr>
              <th v-for="(header, key) in content.meta.fields" :key="'header-' + key">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowKey) in content.data.slice(0, 12)" :key="'row-' + rowKey">
              <td
                v-for="(column, columnKey) in content.meta.fields"
                :key="'row-' + rowKey + '-column-' + columnKey"
              >
                <input v-model="content.data[rowKey][column]" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
    <!-- content -->
    <b-card v-if="loaded" class="small">
      <b-card-title>Applied doses and measurements</b-card-title>
      <b-card-body>
        <p v-if="items.length === 0" class="text-muted fst-italic">
          No entries yet — use "Add row" below or the input panels to add doses and measurements.
        </p>
        <template v-if="items.length > 0">
          <b-table
            id="fb-table"
            striped
            fixed
            small
            responsive="sm"
            :items="items"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
            @row-clicked="onRowClicked"
          >
            <template #cell(datetime)="data">
              <b-input-group v-if="isEditing(data.item, 'datetime')">
                <b-form-input
                  id="editCell"
                  v-model="editCell.newValue"
                  type="text"
                  autocomplete="off"
                  :state="editCell.valid"
                  @input="onInput"
                  @keyup.escape="onEscape"
                  @keyup.enter="onEnter"
                ></b-form-input>
              </b-input-group>
              <span v-else @click.stop="editCellHandler(data.item, 'datetime')">{{
                formatDate(data.item.datetime)
              }}</span>
            </template>
            <template #cell(actions)="{ item, index }">
              <span class="icon-span">
                <i
                  v-if="item.type === 'DOSE'"
                  class="bi bi-copy"
                  style="font-size: 1.4em"
                  title="Duplicate dose"
                  @click.stop="startDuplicate(item)"
                ></i>
                <i
                  class="bi bi-trash"
                  style="font-size: 1.6em"
                  title="Delete"
                  @click="deleteFromTable(item)"
                ></i>
              </span>
            </template>
            <template #cell(amount)="data">
              <b-input-group v-if="isEditing(data.item, 'amount')">
                <b-form-input
                  id="editCell"
                  v-model="editCell.newValue"
                  type="number"
                  :number="true"
                  :state="editCell.valid"
                  class="input-with-unit"
                  placeholder="Amount"
                  aria-describedby="input-1-live-feedback"
                  @input="onInput"
                  @keyup.escape="onEscape"
                  @keyup.enter="onEnter"
                />
                <b-form-select
                  id="editCell"
                  ref="doseUnit"
                  v-model="editCell.newUnit"
                  class="unit-dropdown"
                  value-field="value"
                  text-field="text"
                  :options="unitsForType(data.item.type)"
                  :clearable="false"
                  @input="onInput"
                  @keyup.escape="onEscape"
                  @keyup.enter="onEnter"
                >
                </b-form-select>
              </b-input-group>
              <span v-else @click.stop="editCellHandler(data.item, 'amount')"
                >{{ data.item.amount }} <span v-html="displaySIUnits(data.item.amountUnit)"></span
              ></span>
            </template>
            <template #cell(duration)="data">
              <b-input-group v-if="isEditing(data.item, 'duration')">
                <b-form-input
                  id="editCell"
                  v-model="editCell.newValue"
                  type="number"
                  :number="true"
                  class="input-with-unit"
                  :state="editCell.valid"
                  aria-describedby="input-2-live-feedback"
                  @input="onInput"
                  @keyup.escape="onEscape"
                  @keyup.enter="onEnter"
                />
                <b-form-select
                  id="editCell"
                  ref="durationUnit"
                  v-model="editCell.newUnit"
                  class="unit-dropdown"
                  :options="units.duration"
                  :clearable="false"
                  @input="onInput"
                  @keyup.escape="onEscape"
                  @keyup.enter="onEnter"
                >
                </b-form-select>
              </b-input-group>
              <span v-else @click.stop="editCellHandler(data.item, 'duration')"
                >{{ data.item.duration }}
                <span v-html="displaySIUnits(data.item.durationUnit)"></span
              ></span>
            </template>
            <template #cell(since)="{ item }"
              ><span v-if="item.since" title="hrs since previous dose">{{ item.since }} h</span>
              <span v-if="item.sinceAlert">
                <i class="bi bi-exclamation-triangle icon-red" :title="item.sinceAlert"></i
              ></span>
            </template>
          </b-table>
          <h4><span class="badge bg-danger" v-html="editCell.message"></span></h4>
          <KeepAlive>
            <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              aria-controls="fb-table"
            ></b-pagination>
          </KeepAlive>
        </template>

        <!-- duplicate form -->
        <div v-if="showDuplicateFor !== null" class="action-form mt-2 p-3 border rounded bg-light">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Duplicate row</strong>
            <button type="button" class="btn-close" @click="cancelDuplicate"></button>
          </div>
          <div class="row g-2 align-items-end">
            <div class="col-sm-5">
              <label class="form-label form-label-sm">New date &amp; time</label>
              <input
                v-model="duplicateData.datetime"
                type="datetime-local"
                class="form-control form-control-sm"
              />
            </div>
            <div class="col-sm-3">
              <label class="form-label form-label-sm">Or offset (hours)</label>
              <input
                v-model.number="duplicateData.offset"
                type="number"
                class="form-control form-control-sm"
                placeholder="e.g. 12"
                @input="onDuplicateOffset"
              />
            </div>
            <div class="col-sm-4 d-flex gap-2">
              <b-button size="sm" variant="primary" @click="confirmDuplicate">Duplicate</b-button>
              <b-button size="sm" variant="outline-secondary" @click="cancelDuplicate"
                >Cancel</b-button
              >
            </div>
          </div>
        </div>

        <!-- add row form -->
        <div v-if="showAddRow" class="action-form mt-2 p-3 border rounded bg-light">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Add row</strong>
            <button type="button" class="btn-close" @click="cancelAddRow"></button>
          </div>
          <div class="row g-2 align-items-end">
            <div class="col-sm-3">
              <label class="form-label form-label-sm">Type</label>
              <b-form-select
                v-model="addRow.type"
                :options="addRowTypes"
                size="sm"
                @change="onAddRowTypeChange"
              ></b-form-select>
            </div>
            <div class="col-sm-4">
              <label class="form-label form-label-sm">Amount</label>
              <b-input-group size="sm">
                <b-form-input
                  v-model.number="addRow.amount"
                  type="number"
                  placeholder="Amount"
                  :state="addRow.amountError ? false : null"
                  class="input-with-unit"
                ></b-form-input>
                <b-form-select
                  v-model="addRow.amountUnit"
                  :options="unitsForType(addRow.type)"
                  value-field="value"
                  text-field="text"
                  class="unit-dropdown"
                ></b-form-select>
              </b-input-group>
              <div v-if="addRow.amountError" class="text-danger small">
                {{ addRow.amountError }}
              </div>
            </div>
            <div v-if="addRow.type === 'DOSE'" class="col-sm-3">
              <label class="form-label form-label-sm">Duration</label>
              <b-input-group size="sm">
                <b-form-input
                  v-model.number="addRow.duration"
                  type="number"
                  placeholder="Duration"
                  :state="addRow.durationError ? false : null"
                  class="input-with-unit"
                ></b-form-input>
                <b-form-select
                  v-model="addRow.durationUnit"
                  :options="units.duration"
                  class="unit-dropdown"
                ></b-form-select>
              </b-input-group>
              <div v-if="addRow.durationError" class="text-danger small">
                {{ addRow.durationError }}
              </div>
            </div>
            <div class="col-sm-4">
              <label class="form-label form-label-sm">Date &amp; time</label>
              <input
                v-model="addRow.datetime"
                type="datetime-local"
                class="form-control form-control-sm"
                :class="addRow.datetimeError ? 'is-invalid' : ''"
              />
              <div v-if="addRow.datetimeError" class="invalid-feedback">
                {{ addRow.datetimeError }}
              </div>
            </div>
            <div class="col-sm-2 d-flex gap-2 mt-2">
              <b-button size="sm" variant="primary" @click="submitAddRow">Add</b-button>
              <b-button size="sm" variant="outline-secondary" @click="cancelAddRow"
                >Cancel</b-button
              >
            </div>
          </div>
        </div>

        <!-- add row button -->
        <div v-if="!showAddRow && showDuplicateFor === null" class="mt-2">
          <b-button
            size="sm"
            variant="outline-primary"
            title="Add a new dose, concentration, or creatinine entry"
            @click="openAddRow"
          >
            <i class="bi bi-plus-lg"></i> Add row
          </b-button>
        </div>
      </b-card-body>
      <b-card-footer class="d-flex flex-row-reverse">
        <b-button class="m-2" @click="importModal">IMPORT</b-button>
        <b-button v-if="items.length > 0" class="m-2" @click="exportCSV">EXPORT</b-button>
      </b-card-footer>
    </b-card>
    <div v-else>
      <p>Select a drug model then enter patient doses or select from saved patients</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, toRaw } from 'vue';
import type { TableRowEventObject } from 'bootstrap-vue-next';
import Papa from 'papaparse';
import {
  DATEFORMAT,
  EXPORT_DATEFORMAT,
  ERROR,
  FILESOURCE,
  deduplicateDoses,
  importDoseFromRow,
  newDosesOnly,
  addDoseFeedback,
} from '@/components/services/common';
import {
  formatDisplayDateTime,
  isValidDateValue,
  parseDisplayDateTime,
} from '@/models/utils/datetime';
import moment from 'moment';
import { Dose } from '@/models/Dose';
import { displaySIUnits, DoseTypes } from '@/models/utils/constants';
import { units } from '@/models/utils/constants';
import { validations } from '@/models/utils/validations';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';

defineOptions({ name: 'DosesTable' });

const props = defineProps<{ model: any }>();

const store = useDosingStore();
const toast = useToast();

const showImportModal = ref(false);
const perPage = 15;
const currentPage = ref(1);
const file = ref<File | null>(null);
const content = ref<any>([]);
const parsed = ref(false);
const formats = DATEFORMAT;
const importFormat = ref('DMY');
const sources = FILESOURCE;
const importSource = ref('DOSING');
const importMode = ref<'replace' | 'add'>('replace');
const importKey = ref(0);

const fields = [
  { key: 'datetime', label: 'Date & time', sortable: true, formatter: 'formatDate' },
  { key: 'type', sortable: true },
  { key: 'amount', sortable: true },
  { key: 'duration', sortable: true },
  { key: 'since', sortable: true },
  { key: 'actions', sortable: false },
];

const export_fields: Record<string, any> = {
  Type: 'type',
  Time: { field: 'datetime', callback: (value: any) => moment(value).format(EXPORT_DATEFORMAT) },
  Value: 'amount',
  Value_unit: 'amountUnit',
  Infusion: 'duration',
  Infusion_unit: 'durationUnit',
};

const editCell = ref({
  row: -1,
  field: null as string | null,
  newValue: null as any,
  newUnit: null as string | null,
  valid: null as boolean | null,
  message: '',
});

// ── add row ──────────────────────────────────────────────────────────────────

const showAddRow = ref(false);
const showDuplicateFor = ref<number | null>(null); // source item id

const addRow = reactive({
  type: DoseTypes.DOSE as string,
  amount: null as number | null,
  amountUnit: 'mg',
  duration: 1,
  durationUnit: 'hours',
  datetime: '',
  amountError: '',
  durationError: '',
  datetimeError: '',
});

const addRowTypes = [
  { value: DoseTypes.DOSE, text: 'Dose' },
  { value: DoseTypes.CONC, text: 'Concentration' },
  { value: DoseTypes.SECR, text: 'Serum Creatinine' },
];

const defaultUnitForType: Record<string, string> = {
  [DoseTypes.DOSE]: 'mg',
  [DoseTypes.CONC]: 'mcg/mL',
  [DoseTypes.SECR]: 'umol/L',
};

const rowVariantForType = (type: string) => {
  if (type === DoseTypes.CONC) return 'info';
  if (type === DoseTypes.SECR) return 'success';
  return '';
};

function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function resetAddRow() {
  addRow.type = DoseTypes.DOSE;
  addRow.amount = null;
  addRow.amountUnit = 'mg';
  addRow.duration = 1;
  addRow.durationUnit = 'hours';
  addRow.datetime = toDatetimeLocal(new Date());
  addRow.amountError = '';
  addRow.durationError = '';
  addRow.datetimeError = '';
}

function openAddRow() {
  resetAddRow();
  showAddRow.value = true;
}

function onAddRowTypeChange() {
  addRow.amountUnit = defaultUnitForType[addRow.type] ?? 'mg';
  addRow.amountError = '';
}

function validateAddRow(): boolean {
  let ok = true;

  if (!addRow.datetime) {
    addRow.datetimeError = 'Date and time required';
    ok = false;
  } else {
    addRow.datetimeError = '';
  }

  if (addRow.amount === null || addRow.amount === undefined || isNaN(addRow.amount as any)) {
    addRow.amountError = 'Amount required';
    ok = false;
  } else {
    const unit = addRow.amountUnit;
    let between: [number, number];
    if (addRow.type === DoseTypes.SECR) {
      between = [
        (validations as any).creatinine[unit]?.min ?? 0,
        (validations as any).creatinine[unit]?.max ?? 99999,
      ];
    } else if (addRow.type === DoseTypes.DOSE) {
      const convert = unit === 'g' ? 1000 : 1;
      between = [props.model.default.minDose / convert, props.model.default.maxDose / convert];
    } else {
      between = [0.1, 10000];
    }
    if ((addRow.amount as number) < between[0] || (addRow.amount as number) > between[1]) {
      addRow.amountError = `Must be ${between[0]}–${between[1]} ${displaySIUnits(unit)}`;
      ok = false;
    } else {
      addRow.amountError = '';
    }
  }

  if (addRow.type === DoseTypes.DOSE) {
    const unit = addRow.durationUnit;
    const between = (validations as any).duration[unit];
    if (between && (addRow.duration < between[0] || addRow.duration > between[1])) {
      addRow.durationError = `Must be ${between[0]}–${between[1]} ${displaySIUnits(unit)}`;
      ok = false;
    } else {
      addRow.durationError = '';
    }
  } else {
    addRow.durationError = '';
  }

  return ok;
}

function submitAddRow() {
  if (!validateAddRow()) return;
  const dose = new Dose({
    type: addRow.type,
    amount: addRow.amount!,
    amountUnit: addRow.amountUnit,
    duration: addRow.type === DoseTypes.DOSE ? addRow.duration : (null as any),
    durationUnit: addRow.type === DoseTypes.DOSE ? addRow.durationUnit : (null as any),
    datetime: new Date(addRow.datetime),
    _rowVariant: rowVariantForType(addRow.type),
  });
  reportDuplicate(store.addDose(dose));
  showAddRow.value = false;
  resetAddRow();
}

function cancelAddRow() {
  showAddRow.value = false;
  resetAddRow();
}

// ── duplicate row ─────────────────────────────────────────────────────────────

const duplicateData = reactive({
  datetime: '',
  offset: null as number | null,
  sourceItem: null as any,
});

function startDuplicate(item: any) {
  showAddRow.value = false;
  duplicateData.sourceItem = item;
  duplicateData.datetime = toDatetimeLocal(new Date(item.datetime));
  duplicateData.offset = null;
  showDuplicateFor.value = item.id;
}

function onDuplicateOffset() {
  if (duplicateData.offset !== null && duplicateData.sourceItem) {
    const dt = new Date(duplicateData.sourceItem.datetime);
    dt.setTime(dt.getTime() + (duplicateData.offset ?? 0) * 3_600_000);
    duplicateData.datetime = toDatetimeLocal(dt);
  }
}

function confirmDuplicate() {
  if (!duplicateData.sourceItem || !duplicateData.datetime) return;
  const src = duplicateData.sourceItem;
  const dose = new Dose({
    type: src.type,
    amount: src.amount,
    amountUnit: src.amountUnit,
    duration: src.duration,
    durationUnit: src.durationUnit,
    datetime: new Date(duplicateData.datetime),
    _rowVariant: src._rowVariant ?? rowVariantForType(src.type),
  });
  reportDuplicate(store.addDose(dose));
  showDuplicateFor.value = null;
  duplicateData.sourceItem = null;
  duplicateData.offset = null;
}

function cancelDuplicate() {
  showDuplicateFor.value = null;
  duplicateData.sourceItem = null;
  duplicateData.offset = null;
}

// ── existing logic (unchanged) ────────────────────────────────────────────────

const items = computed(() => store.getDoses ?? []);
const exportFilename = computed(() => {
  const patient = store.getPatient;
  const saved = store.getSelectedId;
  return saved ? `${saved}_export.csv` : patient ? `${patient.id}_export.csv` : 'data_export.csv';
});
const loaded = computed(() => store.getModel !== null);

onMounted(() => {
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') {
      if (editCell.value.row >= 0) resetEditCell();
      if (showAddRow.value) cancelAddRow();
      if (showDuplicateFor.value !== null) cancelDuplicate();
    }
  });
});

function resetEditCell() {
  editCell.value = {
    row: -1,
    field: null,
    newValue: null,
    newUnit: null,
    valid: null,
    message: '',
  };
}

function formatDate(value: any) {
  return formatDisplayDateTime(new Date(value));
}

function deleteFromTable(item: any) {
  store.removeDose(item);
}

// The add/duplicate row has no success toast; only report a skipped duplicate
function reportDuplicate(result: { added: number; skipped: number }) {
  const feedback = addDoseFeedback(result, '');
  if (feedback.type === 'error') toast.error(feedback.text);
}

function importModal() {
  // Start each import fresh, so OK without a new file can't re-import the previous one
  file.value = null;
  content.value = [];
  parsed.value = false;
  importKey.value++;
  importMode.value = 'replace';
  showImportModal.value = true;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function importDoses() {
  if (!parsed.value) {
    toast.error('Data not imported');
    return;
  }
  const start = moment.now();
  const doses: Dose[] = [];
  let invalid = 0;
  let unrecognised = 0;
  content.value.data.forEach((row: any) => {
    const dose = importDoseFromRow(importSource.value, importFormat.value, row, start);
    if (dose instanceof Dose) {
      doses.push(dose);
    } else if (dose === ERROR.INVALID) {
      invalid++;
    } else {
      unrecognised++;
    }
  });

  // One store update, so the simulation and fit run once per import
  let imported = 0;
  if (doses.length > 0) {
    if (importMode.value === 'replace' || items.value.length === 0) {
      const unique = deduplicateDoses(doses);
      store.setDoses(unique);
      imported = unique.length;
    } else {
      // addDose keeps existing doses exactly as they are and skips duplicates
      imported = store.addDose(doses).added;
    }
  }
  const duplicates = doses.length - imported;

  const details = [
    duplicates > 0 ? `${plural(duplicates, 'duplicate')} skipped` : '',
    invalid > 0 ? plural(invalid, 'invalid row') : '',
    unrecognised > 0 ? plural(unrecognised, 'unrecognised row') : '',
  ].filter(Boolean);
  const message = `Imported ${plural(imported, 'dose')}${details.length ? ` (${details.join(', ')})` : ''}`;
  if (imported > 0) {
    toast.success(message);
  } else {
    toast.error(message);
  }
}

function handleFileUpload($event: Event) {
  // Discard any earlier file first, so a rejected or cancelled pick can't import it
  parsed.value = false;
  content.value = [];
  const target = $event.target as HTMLInputElement;
  file.value = target.files?.[0] ?? null;
  if (!file.value) return;
  if (file.value.size < 3000) {
    parseFile();
  } else {
    toast.error('File size is too big (max 3k)');
  }
}

function parseFile() {
  const parsing = file.value;
  const rtn = Papa.parse(parsing!, {
    header: true,
    skipEmptyLines: true,
    complete: function (results: any) {
      // Ignore a parse that finishes after another file was picked (or the pick was cleared)
      if (file.value !== parsing) return;
      content.value = results;
      parsed.value = true;
    },
  });
  if (rtn && rtn.errors && rtn.errors.length > 0) {
    toast.error('Errors during parse');
    console.error(rtn.errors);
  }
}

// b-table's data.index is the position on screen (after sorting/paging), not in the store,
// so rows are identified by their dose object
const rowOf = (item: any) => items.value.findIndex((d: any) => toRaw(d) === toRaw(item));

function isEditing(item: any, field: string) {
  return (
    editCell.value.field === field &&
    editCell.value.row >= 0 &&
    toRaw(items.value[editCell.value.row]) === toRaw(item)
  );
}

function editCellHandler(item: any, field: string) {
  if (editCell.value.valid === false) return;
  // Applying a pending edit can re-sort the doses and replaces the edited entry,
  // so look the row up afterwards (following the replacement if it was this row)
  const applied = editCell.value.row >= 0 ? applyCellEdit() : null;
  const row = rowOf(applied && toRaw(applied.from) === toRaw(item) ? applied.to : item);
  if (row < 0) {
    resetEditCell();
    return;
  }
  if (field === 'duration') {
    if (items.value[row].type === 'DOSE') {
      editCell.value = {
        row,
        field,
        newValue: items.value[row].duration,
        newUnit: items.value[row].durationUnit,
        valid: true,
        message: '',
      };
    } else {
      resetEditCell();
    }
  } else if (field === 'amount') {
    editCell.value = {
      row,
      field,
      newValue: items.value[row].amount,
      newUnit: items.value[row].amountUnit,
      valid: true,
      message: '',
    };
  } else if (field === 'datetime') {
    editCell.value = {
      row,
      field,
      newValue: formatDisplayDateTime(new Date(items.value[row].datetime)),
      newUnit: null,
      valid: null,
      message: '',
    };
  } else {
    resetEditCell();
  }
}

function onEscape() {
  resetEditCell();
}

// Applies the pending edit through the store (an updated copy, not an in-place change).
// Returns the replaced entry and its replacement, so a click on that row can be followed.
function applyCellEdit(): { from: any; to: any } | null {
  if (editCell.value.row < 0) return null;
  const item = items.value[editCell.value.row];
  let changes: Record<string, any> | null = null;
  if (editCell.value.field === 'duration') {
    const duration = Number(editCell.value.newValue);
    if (duration !== Number(item.duration) || editCell.value.newUnit !== item.durationUnit) {
      changes = { duration, durationUnit: editCell.value.newUnit };
    }
  } else if (editCell.value.field === 'amount') {
    const amount = Number(editCell.value.newValue);
    if (amount !== Number(item.amount) || editCell.value.newUnit !== item.amountUnit) {
      changes = { amount, amountUnit: editCell.value.newUnit };
    }
  } else if (editCell.value.field === 'datetime') {
    const edited = parseDisplayDateTime(editCell.value.newValue);
    // The same minute keeps the stored value exactly (including seconds)
    if (edited.getTime() !== moment(item.datetime).startOf('minute').valueOf()) {
      changes = { datetime: edited };
    }
  }
  if (!changes) return null;
  const updated = store.updateDose(item, changes);
  return updated ? { from: item, to: updated } : null;
}

function onRowClicked(_row: TableRowEventObject<any>) {
  if (editCell.value.valid === false) return;
  if (editCell.value.row >= 0) applyCellEdit();
  resetEditCell();
}

function onEnter() {
  if (editCell.value.valid === false) return;
  if (editCell.value.row >= 0) applyCellEdit();
  resetEditCell();
}

function onInput() {
  if (editCell.value.row < 0) return;
  if (editCell.value.field === 'duration') {
    const unit = editCell.value.newUnit!;
    const duration = editCell.value.newValue;
    const between = (validations as any).duration[unit];
    editCell.value.valid = duration >= between[0] && duration <= between[1];
    editCell.value.message = editCell.value.valid
      ? ''
      : `Duration must be between ${between[0]} and ${between[1]} ${displaySIUnits(unit)}`;
  } else if (editCell.value.field === 'amount') {
    const unit = editCell.value.newUnit!;
    const amount = editCell.value.newValue;
    const amountType = items.value[editCell.value.row].type;
    let between: [number, number];
    if (amountType === 'SeCR') {
      between = [
        (validations as any).creatinine[unit].min,
        (validations as any).creatinine[unit].max,
      ];
    } else if (amountType === 'DOSE') {
      const convert = unit === 'g' ? 1000 : 1;
      between = [props.model.default.minDose / convert, props.model.default.maxDose / convert];
    } else if (amountType === 'CONC') {
      between = [0.1, 10000];
    } else {
      editCell.value.valid = null;
      editCell.value.message = '';
      return;
    }
    editCell.value.valid = amount >= between[0] && amount <= between[1];
    editCell.value.message = editCell.value.valid
      ? ''
      : `Amount must be between ${between[0]} and ${between[1]} ${displaySIUnits(unit)}`;
  } else if (editCell.value.field === 'datetime') {
    const item = items.value[editCell.value.row];
    const edited = parseDisplayDateTime(editCell.value.newValue);
    const others = items.value.filter((d: any) => toRaw(d) !== toRaw(item));
    if (!isValidDateValue(edited)) {
      editCell.value.valid = false;
      editCell.value.message = 'Use DD/MM/YYYY HH:mm (24-hour)';
    } else if (newDosesOnly(others, [{ type: item.type, datetime: edited }]).length === 0) {
      // Same rule as adding a dose: one entry per type per minute
      editCell.value.valid = false;
      editCell.value.message = `Another ${item.type} is already at this time`;
    } else {
      editCell.value.valid = true;
      editCell.value.message = '';
    }
  }
}

function unitsForType(type: string) {
  const unitsToMap =
    type === 'DOSE'
      ? units.dose
      : type === 'CONC'
        ? units.concentration
        : units.creatinineConcentration;
  return unitsToMap.map((u: string) => ({ value: u, text: displaySIUnits(u) }));
}

function exportCSV() {
  const headers = Object.keys(export_fields);
  const rows = items.value.map((item: any) => {
    return Object.values(export_fields).map((field: any) => {
      let val;
      if (typeof field === 'string') {
        val = item[field] ?? '';
      } else if (field.field && field.callback) {
        val = field.callback(item[field.field]) ?? '';
      } else {
        val = '';
      }
      return `"${String(val).replace(/"/g, '""')}"`;
    });
  });
  const csv = [headers.join(','), ...rows.map((r: string[]) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = exportFilename.value;
  a.click();
  URL.revokeObjectURL(url);
}
</script>

<style scoped>
.form-label {
  font-weight: bold;
}

.modal-content .custom-control {
  text-align: left;
}

.action-form {
  background-color: #f8f9fa;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
